import { ProductStylingViewModel } from '../../../../../../models/shared/product-styling-view-model';
import { map, shareReplay } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { exists } from '../../../../../../functions/exists';
import { EnumUtils } from '../../../../../../utils/enum-utils';
import { Move, SortUtils } from '../../../../../../utils/sort-utils';
import { SectionColumnConfigCollectiveTerpeneKey } from '../../../../../../models/enum/shared/section-column-config-collective-terpene-key';
import { Cannabinoid, PrimaryCannabinoid } from '../../../../../../models/enum/shared/cannabinoid';
import type { CardData } from '../../../../../../models/print-cards/card-data';
import type { Terpene } from '../../../../../../models/enum/shared/terpene';
import { PrintCardProperty, PrintCardPropertyDefinition } from 'src/app/utils/print-card-property-definition';
import { RegexUtils } from '../../../../../../utils/regex-utils';
import { CompoundUtils } from '../../../../../../utils/compound-utils';
import { StringUtils } from '../../../../../../utils/string-utils';
import { CannabinoidDisplayType } from '../../../../../../models/enum/shared/cannabinoid-display-type.enum';
import { TerpeneDisplayType } from '../../../../../../models/enum/shared/terpene-display-type';
import type { PrintCardColumnViewModel } from '../../../../../display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/print-card-column-view-model';
import type { Variant } from '../../../../../../models/product/dto/variant';

export type CannabinoidOrTerpeneFromPool = { title: string; value: string };
export type GridCannabinoidOrTerpeneFromPool = { title: string; values: string[] };
export type PropertyPoolSorter = (a: CannabinoidOrTerpeneFromPool, b: CannabinoidOrTerpeneFromPool) => number;

export abstract class CardDataWithPropertyPooling extends ProductStylingViewModel {
  public override rowViewModel$: Observable<CardData>;

  public readonly cardData$ = this.rowViewModel$.pipe(
    map(cardData => cardData ?? null),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly adminSetVisibleProperties$ = this.cardData$.pipe(
    map(cardData => {
      const cardSize = cardData?.menu?.metadata?.printCardSize;
      const adminSetVisibleProperties =
        cardData?.menu?.hydratedTheme?.printConfig?.visiblePropertyMap.get(cardSize) ?? [];
      return PrintCardPropertyDefinition.primaryPropertyList.filter(property => {
        return adminSetVisibleProperties.includes(property);
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly helperTotalTerpenes$: Observable<CannabinoidOrTerpeneFromPool | null> = combineLatest([
    this.cardData$,
    this.adminSetVisibleProperties$
  ]).pipe(
    map(([cardData, adminSetVisibleProperties]) => {
      const totalTerpsVisible = adminSetVisibleProperties.includes(PrintCardProperty.TotalTerpene);
      if (!totalTerpsVisible) return null;
      const totalTerpenes = cardData?.getTerpene(SectionColumnConfigCollectiveTerpeneKey.TotalTerpene);
      if (this.propertyAsStringExists(totalTerpenes)) {
        return { title: this.totalTerpenesPropertyTitle(), value: totalTerpenes };
      }
      const emptyPropertyValue = this.emptyPropertyValueForPropertiesBasedOnPools();
      if (exists(emptyPropertyValue)) return { title: this.totalTerpenesPropertyTitle(), value: emptyPropertyValue };
      return null;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly helperTAC$: Observable<CannabinoidOrTerpeneFromPool | null> = combineLatest([
    this.cardData$,
    this.adminSetVisibleProperties$
  ]).pipe(
    map(([cardData, adminSetVisibleProperties]) => {
      const tacVisible = adminSetVisibleProperties.includes(PrintCardProperty.TAC);
      if (!tacVisible) return null;
      const tac = cardData?.getTotalActiveCannabinoids();
      if (this.propertyAsStringExists(tac)) {
        return { title: this.tacPropertyTitle(), value: tac };
      }
      const emptyPropertyValue = this.emptyPropertyValueForPropertiesBasedOnPools();
      if (exists(emptyPropertyValue)) return { title: this.tacPropertyTitle(), value: emptyPropertyValue };
      return null;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly helperTopTerp$: Observable<CannabinoidOrTerpeneFromPool | null> = combineLatest([
    this.cardData$,
    this.adminSetVisibleProperties$
  ]).pipe(
    map(([cardData, adminSetVisibleProperties]) => {
      const topTerpVisible = adminSetVisibleProperties.includes(PrintCardProperty.TopTerpene);
      if (!topTerpVisible) return null;
      const topTerp = cardData?.getTopTerpene();
      if (this.propertyAsStringExists(topTerp)) {
        return { title: this.topTerpenePropertyTitle(), value: topTerp };
      }
      const emptyPropertyValue = this.emptyPropertyValueForPropertiesBasedOnPools();
      if (exists(emptyPropertyValue)) return { title: this.topTerpenePropertyTitle(), value: emptyPropertyValue };
      return null;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly helperPresentTerpenes$: Observable<CannabinoidOrTerpeneFromPool | null> = combineLatest([
    this.cardData$,
    this.adminSetVisibleProperties$
  ]).pipe(
    map(([cardData, adminSetVisibleProperties]) => {
      const presentTerpsVisible = adminSetVisibleProperties.includes(PrintCardProperty.PresentTerpenes);
      if (!presentTerpsVisible) return null;
      const presentTerps = cardData?.getPresentTerpenes()?.filter(terp => this.propertyAsStringExists(terp)) ?? [];
      if (presentTerps.length) {
        return { title: this.presentTerpenesPropertyTitle(), value: presentTerps.join(', ') };
      }
      const emptyPropertyValue = this.emptyPropertyValueForPropertiesBasedOnPools();
      if (exists(emptyPropertyValue)) return { title: this.presentTerpenesPropertyTitle(), value: emptyPropertyValue };
      return null;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly maxNumberOfPropertiesToUseForPropertyBasedPooling$ = this.cardData$.pipe(
    map(cardData => {
      const forcedMaxProps = this.forceMaxPropsTo();
      if (forcedMaxProps) return forcedMaxProps;
      return this.getMaxNumberOfPropertiesToUseForPropertyBasedPooling(cardData);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly allPotentialPropertiesForSecondaryPool$: Observable<CannabinoidOrTerpeneFromPool[]> =
    this.cardData$.pipe(
      map(cardData => {
        return cardData?.isNonCannabinoidVariant() ? [] : this.getAllPotentialPropertiesForSecondaryPool(cardData);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  public readonly maxPropLimitedVisibleSecondaryProperties$ = combineLatest([
    this.maxNumberOfPropertiesToUseForPropertyBasedPooling$,
    this.allPotentialPropertiesForSecondaryPool$
  ]).pipe(
    map(([maxProps, properties]) => {
      return properties?.take(maxProps);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly primaryPoolOfCompoundProperties$ = combineLatest([
    this.cardData$,
    this.maxNumberOfPropertiesToUseForPropertyBasedPooling$,
    this.helperTotalTerpenes$,
    this.helperTAC$,
    this.helperTopTerp$,
    this.helperPresentTerpenes$
  ]).pipe(
    map(([cardData, maxProps, totalTerpenes, TAC, topTerp, presentTerps]) => {
      return this.getPrimaryPoolOfCompoundProperties(cardData, maxProps, totalTerpenes, TAC, topTerp, presentTerps);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly secondaryPoolOfCompoundProperties$ = combineLatest([
    this.maxNumberOfPropertiesToUseForPropertyBasedPooling$,
    this.primaryPoolOfCompoundProperties$,
    this.allPotentialPropertiesForSecondaryPool$
  ]).pipe(
    map(([maxProps, primaryProperties, visibleSecondaryProperties]) => {
      const remainingPropsSpaces = maxProps - primaryProperties.length;
      if (remainingPropsSpaces <= 0) return [];
      return visibleSecondaryProperties?.take(remainingPropsSpaces);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly propertiesBasedOnPools$ = combineLatest([
    this.primaryPoolOfCompoundProperties$,
    this.secondaryPoolOfCompoundProperties$
  ]).pipe(
    map(([primaryProperties, secondaryProperties]) => {
      const combinedProperties = [...primaryProperties];
      if (secondaryProperties?.length) {
        combinedProperties.push(...secondaryProperties);
      }
      const sortFinalListOfCompoundProperties = this.finalSortLambdaForCompoundProperties();
      if (sortFinalListOfCompoundProperties) {
        combinedProperties.sort(sortFinalListOfCompoundProperties);
      }
      const emptyPropertyValue = this.emptyPropertyValueForPropertiesBasedOnPools();
      if (exists(emptyPropertyValue)) {
        return combinedProperties?.map(prop => this.withEmptyPropertyValueIfNeeded(prop, emptyPropertyValue));
      }
      return combinedProperties?.filter(prop => !this.propertyNumericValueIsTooSmall(prop));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasPropertiesBasedOnPools$ = this.propertiesBasedOnPools$.pipe(
    map(properties => properties?.length > 0),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly visibleTotalTerpenes$ = this.getVisibleProp$(this.totalTerpenesPropertyTitle());

  public readonly visibleTAC$ = this.getVisibleProp$(this.tacPropertyTitle());

  public readonly visibleTopTerp$ = this.getVisibleProp$(this.topTerpenePropertyTitle());

  public readonly visiblePresentTerpenes$ = this.getVisibleProp$(this.presentTerpenesPropertyTitle());

  protected getGridPropertiesBasedOnPools$(
    columnVms$: Observable<PrintCardColumnViewModel[]>
  ): Observable<GridCannabinoidOrTerpeneFromPool[]> {
    return combineLatest([this.propertiesBasedOnPools$, this.cardData$, columnVms$]).pipe(
      map(([properties, cardData, columnVms]) => this.getGridPropertiesBasedOnPools(properties, cardData, columnVms)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  protected totalTerpenesPropertyTitle(): string {
    return 'TERPS';
  }

  protected tacPropertyTitle(): string {
    return 'TAC';
  }

  protected topTerpenePropertyTitle(): string {
    return 'TOP TERP';
  }

  protected presentTerpenesPropertyTitle(): string {
    return 'PRESENT TERPS';
  }

  protected getVisibleProp$(title: string): Observable<CannabinoidOrTerpeneFromPool | null> {
    return this.propertiesBasedOnPools$.pipe(
      map(properties => properties?.find(prop => prop?.title === title) ?? null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  protected forceMaxPropsTo(): number | null {
    return null;
  }

  protected getMaxNumberOfPropertiesToUseForPropertyBasedPooling(cardData: CardData): number {
    const numVisibleCompounds = cardData?.menu?.hydratedTheme?.printConfig?.visibleCompoundMap;
    const cardSize = cardData?.menu?.metadata?.printCardSize;
    return numVisibleCompounds?.get(cardSize) ?? 0;
  }

  protected emptyPropertyValueForPropertiesBasedOnPools(): string | null {
    return null;
  }

  protected getPrimaryPoolOfCompoundProperties(
    cardData: CardData,
    maxProps: number,
    visibleTotalTerpenes: CannabinoidOrTerpeneFromPool | null,
    visibleTAC: CannabinoidOrTerpeneFromPool | null,
    visibleTopTerp: CannabinoidOrTerpeneFromPool | null,
    visiblePresentTerps: CannabinoidOrTerpeneFromPool | null
  ): CannabinoidOrTerpeneFromPool[] {
    if (cardData?.isNonCannabinoidVariant()) return [];
    const properties: CannabinoidOrTerpeneFromPool[] = [];
    this.addVisibleTotalTerpenesToPrimaryPool(visibleTotalTerpenes, maxProps, properties);
    this.addVisibleTACToPrimaryPool(visibleTAC, maxProps, properties);
    this.addVisibleTopTerpeneToPrimaryPool(visibleTopTerp, maxProps, properties);
    this.addVisiblePresentTerpsToPrimaryPool(visiblePresentTerps, maxProps, properties);
    return properties;
  }

  private getGridPropertiesBasedOnPools(
    properties: CannabinoidOrTerpeneFromPool[],
    cardData: CardData,
    columnVms: PrintCardColumnViewModel[]
  ): GridCannabinoidOrTerpeneFromPool[] {
    return properties
      ?.filter(property => this.isSupportedGridProperty(property?.title))
      ?.map(property => this.getGridCompoundProperty(cardData, columnVms, property.title))
      ?.filter(property => exists(property));
  }

  protected addVisibleTotalTerpenesToPrimaryPool(
    totalTerpenes: CannabinoidOrTerpeneFromPool | null,
    maxProps: number,
    properties: CannabinoidOrTerpeneFromPool[]
  ): void {
    if (maxProps - properties.length < 1 || !totalTerpenes) return;
    properties.push(totalTerpenes);
  }

  protected addVisibleTACToPrimaryPool(
    visibleTAC: CannabinoidOrTerpeneFromPool | null,
    maxProps: number,
    properties: CannabinoidOrTerpeneFromPool[]
  ): void {
    if (maxProps - properties.length < 1 || !visibleTAC) return;
    properties.push(visibleTAC);
  }

  protected addVisibleTopTerpeneToPrimaryPool(
    visibleTopTerpene: CannabinoidOrTerpeneFromPool | null,
    maxProps: number,
    properties: CannabinoidOrTerpeneFromPool[]
  ): void {
    if (maxProps - properties.length < 1 || !visibleTopTerpene) return;
    properties.push(visibleTopTerpene);
  }

  protected addVisiblePresentTerpsToPrimaryPool(
    visiblePresentTerps: CannabinoidOrTerpeneFromPool | null,
    maxProps: number,
    properties: CannabinoidOrTerpeneFromPool[]
  ): void {
    if (maxProps - properties.length < 1 || !visiblePresentTerps) return;
    properties.push(visiblePresentTerps);
  }

  /**
   * const x = ['1', '2', '4', '3', '0.001', '', '-', '--', null, undefined];
   * x.filter(exists).sort((a, b) => SortUtils.parseFloatFromStringAscending(a, b));
   *   ["0.001", "1", "2", "3", "4", "-", "--"]
   * x.filter(exists).sort((a, b) => SortUtils.parseFloatFromStringDescending(a, b));
   *   ["4", "3", "2", "1", "0.001", "-", "--"]
   */
  protected getAllPotentialPropertiesForSecondaryPool(cardData: CardData): CannabinoidOrTerpeneFromPool[] {
    const properties: CannabinoidOrTerpeneFromPool[] = [];
    const propertiesToLookThrough = this.whatPropertiesToUseForSecondaryPoolOfCompoundProperties(cardData);
    const allCannabinoidKeys = EnumUtils.getCannabinoids();
    const allTerpeneKeys = EnumUtils.getTerpenes();
    propertiesToLookThrough?.forEach(cannabinoidOrTerpene => {
      if (allCannabinoidKeys.includes(cannabinoidOrTerpene as Cannabinoid)) {
        properties.push({ title: cannabinoidOrTerpene, value: cardData?.getCannabinoid(cannabinoidOrTerpene) });
      } else if (allTerpeneKeys.includes(cannabinoidOrTerpene as Terpene)) {
        properties.push({ title: cannabinoidOrTerpene, value: cardData?.getTerpene(cannabinoidOrTerpene) });
      }
    });
    return properties
      ?.filter(this.getSecondaryPoolOfCompoundPropertiesFilterLambda())
      ?.sort(this.getSecondaryPoolOfCompoundPropertiesSortLambda());
  }

  /**
   * Secondary pool includes all cannabinoids and terpenes
   */
  protected whatPropertiesToUseForSecondaryPoolOfCompoundProperties(cardData: CardData): (Cannabinoid | Terpene)[] {
    const result: (Cannabinoid | Terpene)[] = [];
    const cardSize = cardData?.menu?.metadata?.printCardSize;
    const adminSetVisibleProperties =
      cardData?.menu?.hydratedTheme?.printConfig?.visiblePropertyMap.get(cardSize) ?? [];
    const cbdAdminEnabled = adminSetVisibleProperties.includes(PrintCardProperty.CBD);
    const thcAdminEnabled = adminSetVisibleProperties.includes(PrintCardProperty.THC);
    const secondaryCannabinoidsAdminEnabled = adminSetVisibleProperties.includes(
      PrintCardProperty.EnabledSecondaryCannabinoids
    );
    const individualTerpenesAdminEnabled = adminSetVisibleProperties.includes(PrintCardProperty.EnabledTerpenes);
    if (cbdAdminEnabled) result.push(PrimaryCannabinoid.CBD);
    if (thcAdminEnabled) result.push(PrimaryCannabinoid.THC);
    if (secondaryCannabinoidsAdminEnabled)
      result.push(...(cardData?.companyConfig?.enabledSecondaryCannabinoids || []));
    if (individualTerpenesAdminEnabled) result.push(...((cardData?.companyConfig?.enabledTerpenes || []) as Terpene[]));
    return result;
  }

  protected finalSortLambdaForCompoundProperties(): PropertyPoolSorter | null {
    return null;
  }

  protected propertyAsStringExists(value: string): boolean {
    return exists(value) && value !== '-' && value !== '--';
  }

  protected getSecondaryPoolOfCompoundPropertiesFilterLambda(): (a: CannabinoidOrTerpeneFromPool) => boolean {
    return (a: CannabinoidOrTerpeneFromPool) => {
      if (exists(this.emptyPropertyValueForPropertiesBasedOnPools())) return exists(a?.title);
      return this.propertyAsStringExists(a?.value);
    };
  }

  protected getSecondaryPoolOfCompoundPropertiesSortLambda(): PropertyPoolSorter {
    return (a: CannabinoidOrTerpeneFromPool, b: CannabinoidOrTerpeneFromPool) => {
      const aValue = parseFloat(a?.value);
      const bValue = parseFloat(b?.value);
      const aIsNaN = Number.isNaN(aValue);
      const bIsNaN = Number.isNaN(bValue);
      if (aIsNaN && bIsNaN) return Move.Nothing;
      if (aIsNaN) return Move.ARight;
      if (bIsNaN) return Move.BRight;
      return SortUtils.numberDescending(aValue, bValue);
    };
  }

  private getGridCompoundProperty(
    cardData: CardData,
    columnVms: PrintCardColumnViewModel[],
    title: string
  ): GridCannabinoidOrTerpeneFromPool | null {
    const rawValues =
      columnVms?.map(columnVm => {
        const variant = cardData?.getVariantFromGridColumn(columnVm);
        return this.getGridCompoundPropertyValue(cardData, variant, title);
      }) ?? [];
    const hasDisplayableValue = rawValues.some(value => this.gridPropertyValueShouldBeIncluded({ title, value }));
    if (!hasDisplayableValue && !this.emptyPropertyValueForPropertiesBasedOnPools()) return null;
    return {
      title,
      values: rawValues.map(value => this.getDisplayValueForGridPropertyValue({ title, value }))
    };
  }

  private getGridCompoundPropertyValue(cardData: CardData, variant: Variant, title: string): string {
    if (!variant) return null;
    if (title === this.topTerpenePropertyTitle()) {
      return variant?.getTopTerpene(cardData?.companyConfig?.enabledTerpenes);
    }
    if (title === this.presentTerpenesPropertyTitle()) {
      return this.getGridPresentTerpenesValue(cardData, variant);
    }
    if (title === this.totalTerpenesPropertyTitle()) {
      return this.getGridTotalActiveTerpenesValue(cardData, variant);
    }
    if (title === this.tacPropertyTitle()) {
      return this.getGridTotalActiveCannabinoidsValue(cardData, variant);
    }
    if (EnumUtils.getTerpenes().includes(title as Terpene)) {
      return this.getGridIndividualTerpeneValue(cardData, variant, title);
    }
    return this.getGridIndividualCannabinoidValue(cardData, variant, title);
  }

  private getGridIndividualCannabinoidValue(cardData: CardData, variant: Variant, title: string): string {
    if (!variant?.shouldDisplayCannabinoidValue()) return '-';
    const unitOfMeasureString = variant?.getCannabinoidUnitOfMeasureString(cardData?.menu);
    const companyUsesCannabinoidRange =
      cardData?.companyConfig?.cannabinoidDisplayType === CannabinoidDisplayType.Range;
    const displayCannabinoidInRanges = companyUsesCannabinoidRange || variant?.useCannabinoidRange;
    return this.getGridIndividualCompoundValue(title, variant, unitOfMeasureString, displayCannabinoidInRanges);
  }

  private getGridIndividualTerpeneValue(cardData: CardData, variant: Variant, title: string): string {
    if (!variant?.shouldDisplayTerpeneValue()) return '-';
    const unitOfMeasureString = variant?.getTerpeneUnitOfMeasureString(cardData?.menu);
    const companyUsesTerpeneRange = cardData?.companyConfig?.terpeneDisplayType === TerpeneDisplayType.Range;
    const displayTerpeneInRanges = companyUsesTerpeneRange || variant?.useTerpeneRange;
    return this.getGridIndividualCompoundValue(
      StringUtils.camelize(title),
      variant,
      unitOfMeasureString,
      displayTerpeneInRanges
    );
  }

  private getGridIndividualCompoundValue(
    compoundCamelCased: string,
    variant: Variant,
    unitOfMeasureString: string,
    displayInRanges: boolean
  ): string {
    const values = displayInRanges
      ? [
          variant?.getNumericMinCannabinoidOrTerpene(compoundCamelCased),
          variant?.getNumericMaxCannabinoidOrTerpene(compoundCamelCased)
        ]
      : [variant?.getNumericCannabinoidOrTerpene(compoundCamelCased)];
    return this.getGridCompoundValue(values, unitOfMeasureString);
  }

  private getGridTotalActiveCannabinoidsValue(cardData: CardData, variant: Variant): string {
    const enabledSecondaryCannabinoids = cardData?.companyConfig?.enabledSecondaryCannabinoids || [];
    const values = variant?.useCannabinoidRange
      ? [
          variant?.getNumericMinTotalActiveCannabinoids(enabledSecondaryCannabinoids),
          variant?.getNumericMaxTotalActiveCannabinoids(enabledSecondaryCannabinoids)
        ]
      : [variant?.getNumericTotalActiveCannabinoids(enabledSecondaryCannabinoids)];
    return this.getGridCompoundValue(values, variant?.getCannabinoidUnitOfMeasureString(cardData?.menu));
  }

  private getGridTotalActiveTerpenesValue(cardData: CardData, variant: Variant): string {
    const enabledTerpenes = cardData?.companyConfig?.enabledTerpenes || [];
    const values = variant?.useTerpeneRange
      ? [
          variant?.getNumericMinTotalActiveTerpenes(enabledTerpenes),
          variant?.getNumericMaxTotalActiveTerpenes(enabledTerpenes)
        ]
      : [variant?.getNumericTotalActiveTerpenes(enabledTerpenes)];
    return this.getGridCompoundValue(values, variant?.getTerpeneUnitOfMeasureString(cardData?.menu));
  }

  private getGridPresentTerpenesValue(cardData: CardData, variant: Variant): string {
    return variant?.getPresentTerpenes(cardData?.companyConfig?.enabledTerpenes)?.join(', ');
  }

  private getGridCompoundValue(values: number[], unitOfMeasureString: string): string {
    const presentValues = values?.filter(value => Number.isFinite(value) && value >= 0) ?? [];
    const largestValue = presentValues.length ? Math.max(...presentValues) : null;
    if (!Number.isFinite(largestValue) || largestValue < CompoundUtils.smallestAllowableValue) return '-';
    const uniqueValues = presentValues
      ?.map(value => Math.round((value + Number.EPSILON) * 100) / 100)
      ?.unique(false)
      ?.sort(SortUtils.numberAscending);
    const value =
      uniqueValues?.length >= 2
        ? `${uniqueValues.firstOrNull()} - ${uniqueValues.last()}`
        : `${uniqueValues?.firstOrNull() ?? ''}`;
    return value ? `${value}${unitOfMeasureString ?? ''}`.trim() : '-';
  }

  private isSupportedGridProperty(title: string): boolean {
    return (
      title === PrimaryCannabinoid.THC ||
      title === PrimaryCannabinoid.CBD ||
      title === this.totalTerpenesPropertyTitle() ||
      title === this.tacPropertyTitle() ||
      title === this.topTerpenePropertyTitle() ||
      title === this.presentTerpenesPropertyTitle() ||
      EnumUtils.getTerpenes().includes(title as Terpene) ||
      EnumUtils.getSecondaryCannabinoids().includes(title as Cannabinoid)
    );
  }

  private getDisplayValueForGridPropertyValue(prop: CannabinoidOrTerpeneFromPool): string {
    if (this.gridPropertyValueShouldBeIncluded(prop)) return prop.value;
    return this.emptyPropertyValueForPropertiesBasedOnPools() ?? '';
  }

  private gridPropertyValueShouldBeIncluded(prop: CannabinoidOrTerpeneFromPool): boolean {
    if (!this.propertyAsStringExists(prop?.value)) return false;
    if (this.isStringGridProperty(prop?.title)) return true;
    return !this.propertyNumericValueIsTooSmall(prop);
  }

  private isStringGridProperty(title: string): boolean {
    return title === this.topTerpenePropertyTitle() || title === this.presentTerpenesPropertyTitle();
  }

  protected withEmptyPropertyValueIfNeeded(
    prop: CannabinoidOrTerpeneFromPool,
    emptyPropertyValue: string
  ): CannabinoidOrTerpeneFromPool {
    if (!this.propertyAsStringExists(prop?.value) || this.propertyNumericValueIsTooSmall(prop)) {
      return { ...prop, value: emptyPropertyValue };
    }
    return prop;
  }

  protected propertyNumericValueIsTooSmall(prop: CannabinoidOrTerpeneFromPool): boolean {
    const numberString = prop?.value?.match(RegexUtils.parseNumbers)?.last(); // max if ranged
    const parsedNumericValue = Number.parseFloat(numberString);
    const numericValue: number | null = Number.isNaN(parsedNumericValue) ? null : parsedNumericValue;
    return Number.isFinite(numericValue) && numericValue < CompoundUtils.smallestAllowableValue;
  }
}
